import React from 'react';
import { NextoneOrderClientContext } from '../../context/NextoneOrderClientContext';
import CopyrightTransferTypeSelectFormView from './CopyrightTransferTypeSelectFormView';
import CopyrightCollectFormView1 from './CopyrightCollectFormView1';
import CopyrightCollectFormView2 from './CopyrightCollectFormView2';
import CopyrightCollectFormView4EmailPreviewPopup from './CopyrightCollectFormView4EmailPreviewPopup';
import CopyrightCollectFormView3 from './CopyrightCollectFormView3';
import IndependentCopyrightTransferFormView1 from './IndependentCopyrightTransferFormView1';
import IndependentCopyrightTransferFormView2 from './IndependentCopyrightTransferFormView2';
import CopyrightAlreadyTransferredConfirmPopup from './CopyrightAlreadyTransferredConfirmPopup';
import RecipientNameLegalConfirmPopup from './RecipientNameLegalConfirmPopup';
import useCustomForm, { validateField } from '../../../common/hooks/CustomForm';
import ValidationUtility from '../../../common/utility/ValidationUtility';
import { FieldValidationErrorsType, showErrorFlush } from '../../../common/form/FrekulForm';

export default function CopyrightTransferForm({
  formHash,
  karaokeOrder,
  copyrightCollectDocument,
  independentCopyrightTransferDocument,
} : {
  formHash: string,
  copyrightCollectDocument: string,
  independentCopyrightTransferDocument: string,
  karaokeOrder: {
    title: string,
    lyric_writer_name: string,
    composer_name: string,
    lyric_writer_name_kana: string,
    composer_name_kana: string,
    applicant_name: string,
  },
}) {
  type ViewName =
    'selectionView1'|'collectionView1'|'collectionView2'|'collectionView3'|
    'independentView1'|'independentView2';
  type PopupName = 'confirm'|'PreviewEmailPopup'|'RecipientNameLegalConfirm';
  type AjaxName = 'updateArtistWork'|
    'validateCollectStep1'|'validateCollectStep2'|'executeCollect'|
    'validateIndependent'|'executeIndependent';

  const [view, setView] = React.useState<ViewName>('selectionView1');
  const [popup, setPopup] = React.useState<PopupName|null>(null);
  const [ajax, setAjax] = React.useState<AjaxName|null>(null);
  const [previewDocument, setPreviewDocument] = React.useState<string|null>(null);
  const [previewEmailSubject, setPreviewEmailSubject] = React.useState<string|null>(null);
  const [previewEmailBody, setPreviewEmailBody] = React.useState<string|null>(null);

  type FieldName = 'copyright_transfer_type'|
  'lyric_writer_name_kana'|'composer_name_kana'|'applicant_address';
  type ValuesType = { [name in FieldName]: string };
  type ErrorsType = { [name in FieldName]?: FieldValidationErrorsType };
  const [values, errors, setValues, setErrors] = useCustomForm<ValuesType, ErrorsType, { name: FieldName }>({
    defaultValue: {
      copyright_transfer_type: '',
      lyric_writer_name_kana: karaokeOrder.lyric_writer_name_kana || '',
      composer_name_kana: karaokeOrder.composer_name_kana || '',
      applicant_address: '',
    },
    validate(values, options, errors): ErrorsType {
      const name = options.name;
      const value = values[name];
      return { ...errors, [name]: validateField([
        ['copyright_transfer_type', 'notEmpty', '選択してください。', ValidationUtility.isNotEmptyText],
        ['lyric_writer_name_kana', 'notEmpty', '作詞者読みを入力してください。', ValidationUtility.isNotEmptyText],
        ['lyric_writer_name_kana', 'isValidKana', '作詞者読みはひらがなで入力してください。', ValidationUtility.isValidTextKana],
        ['composer_name_kana', 'notEmpty', '作曲者読みを入力してください。', ValidationUtility.isNotEmptyText],
        ['composer_name_kana', 'isValidKana', '作曲者読みはひらがなで入力してください。', ValidationUtility.isValidTextKana],
        ['applicant_address', 'notEmpty', '住所を入力してください。', ValidationUtility.isNotEmptyText],
      ], name, value) };
    },
  })

  type KaraokeAuthorFieldName = 'pseudonym'|'email';
  type KaraokeAuthorValuesType = { [name in KaraokeAuthorFieldName]: string }[];
  type KaraokeAuthorErrorsType = { [name in KaraokeAuthorFieldName]?: FieldValidationErrorsType }[];
  const [
    karaokeAuthorValues,
    karaokeAuthorErrors,
    setKaraokeAuthorValues,
    setKaraokeAuthorErrors,
  ] = useCustomForm<
    KaraokeAuthorValuesType,
    KaraokeAuthorErrorsType,
    { type: 'set', name: KaraokeAuthorFieldName, index: number }|{ type: 'add' }
  >({
    defaultValue: [{
      pseudonym: '',
      email: '',
    }],
    validate(valuesArray, options, karaokeAuthorErrors): KaraokeAuthorErrorsType {
      if (options.type === 'add') {
        return karaokeAuthorErrors || [];
      }
      const name = options.name;
      const index = options.index;
      const value = valuesArray[index][name];
      const newKaraokeAuthorErrors = [...karaokeAuthorErrors || []];
      newKaraokeAuthorErrors[index] = { ...newKaraokeAuthorErrors[index], [name]: validateField([
        ['pseudonym', 'notEmpty', '筆名を入力してください。', ValidationUtility.isNotEmptyText],
        ['email', 'notEmpty', 'メールアドレスを入力してください。', ValidationUtility.isNotEmptyText],
        ['email', 'isValidEn', 'メールアドレスは半角英数字で入力してください。', ValidationUtility.isValidTextEn],
      ], name, value) };
      return newKaraokeAuthorErrors;
    },
  });

  const {
    updateArtistWork,
    validateCopyrightCollectStep1,
    validateCopyrightCollectStep2,
    executeCopyrightCollect,
    validateIndependentCopyrightTransfer,
    executeIndependentCopyrightTransfer,
  } = React.useContext(NextoneOrderClientContext);

  React.useEffect(() => {
    if (ajax === 'updateArtistWork') {
      updateArtistWork(formHash, {
        lyric_writer_name_kana: values.lyric_writer_name_kana,
        composer_name_kana: values.composer_name_kana,
      }).then((responseData) => {
        setAjax(null);
        setErrors(responseData.validationErrors || {});
        if (responseData.result) {
          if (values.copyright_transfer_type === '1') {
            setView('collectionView1');
          } else if (values.copyright_transfer_type === '2') {
            setPopup('confirm');
          } else {
            setView('independentView1');
          }
        } else {
          showErrorFlush('入力内容に不備があります。')
        }
      });
    } else if (ajax === 'validateCollectStep1') {
      validateCopyrightCollectStep1(formHash, { ...values, karaoke_authors: karaokeAuthorValues }).then((responseData) => {
        setAjax(null);
        setErrors(responseData.validationErrors || {});
        setKaraokeAuthorErrors(responseData.validationErrors?.karaoke_authors as KaraokeAuthorErrorsType || []);
        if (responseData.result) {
          setView('collectionView2');
        } else {
          showErrorFlush('入力内容に不備があります。')
        }
      });
    } else if (ajax === 'validateCollectStep2') {
      validateCopyrightCollectStep2(formHash, { ...values, karaoke_authors: karaokeAuthorValues }).then((responseData) => {
        setAjax(null);
        setErrors(responseData.validationErrors || {});
        setKaraokeAuthorErrors(responseData.validationErrors?.karaoke_authors as KaraokeAuthorErrorsType || []);
        if (responseData.result) {
          setPreviewDocument(responseData.document);
          setPreviewEmailSubject(responseData.emailSubject);
          setPreviewEmailBody(responseData.emailBody);
          setView('collectionView3');
        } else {
          showErrorFlush('入力内容に不備があります。')
        }
      });
    } else if (ajax === 'executeCollect') {
      executeCopyrightCollect(formHash, { ...values, karaoke_authors: karaokeAuthorValues }).then((responseData) => {
        setAjax(null);
        setErrors(responseData.validationErrors || {});
        setKaraokeAuthorErrors(responseData.validationErrors?.karaoke_authors as KaraokeAuthorErrorsType || []);
        if (responseData.result) {
          window.location.href = `/nextone_orders/copyright_collect_completed/${formHash}`;
        } else {
          showErrorFlush('入力内容に不備があります。')
        }
      });
    } else if (ajax === 'validateIndependent') {
      validateIndependentCopyrightTransfer(formHash, {
        copyright_transfer_type: values.copyright_transfer_type,
        applicant_address: values.applicant_address,
      }).then((responseData) => {
        setAjax(null);
        setErrors(responseData.validationErrors || {});
        if (responseData.result) {
          setPreviewDocument(responseData.document);
          setView('independentView2');
        } else {
          showErrorFlush('入力内容に不備があります。')
        }
      });
    } else if (ajax === 'executeIndependent') {
      executeIndependentCopyrightTransfer(formHash, {
        copyright_transfer_type: values.copyright_transfer_type,
        applicant_address: values.applicant_address,
      }).then((responseData) => {
        setAjax(null);
        setErrors(responseData.validationErrors || {});
        if (responseData.result) {
          window.location.href = `/nextone_orders/independent_copyright_transfer_completed/${formHash}`;
        } else {
          showErrorFlush('入力内容に不備があります。')
        }
      });
    }
  }, [ajax]);

  const topRef = React.useRef(null);
  React.useEffect(() => {
    (topRef.current as HTMLElement|null)?.scrollIntoView();
  }, [view]);

  return (
    <>
      <div ref={topRef} />
      {view === 'selectionView1' && (
        <CopyrightTransferTypeSelectFormView
          getValue={(name) => values[name]}
          setValue={(name, value) => setValues({ ...values, [name]: value }, { name })}
          getErrors={(name) => errors?.[name] || {}}
          karaokeOrder={karaokeOrder}
          onNext={() => {
            setAjax('updateArtistWork');
          }}
        />
      )}
      {view === 'collectionView1' && (
        <CopyrightCollectFormView1
          getValue={(name) => values[name]}
          addItem={(collection) => setKaraokeAuthorValues([...karaokeAuthorValues, { pseudonym: '', email: '' }], { type: 'add' })}
          getItemCount={(collection) => karaokeAuthorValues.length}
          getItemCountMax={(collection) => 10}
          getItemValue={(collection, index, name) => karaokeAuthorValues[index][name]}
          setItemValue={(collection, index, name, value) => {
            const newKaraokeAuthorValues = [...karaokeAuthorValues];
            newKaraokeAuthorValues[index] = { ...newKaraokeAuthorValues[index], [name]: value };
            setKaraokeAuthorValues(newKaraokeAuthorValues, { type: 'set', name, index });
        }}
          getItemErrors={(collection, index, name) => karaokeAuthorErrors?.[index]?.[name] || {}}
          onBack={() => {
            setView('selectionView1');
          }}
          onNext={() => {
            setAjax('validateCollectStep1');
          }}
        />
      )}
      {view === 'collectionView2' && (
        <CopyrightCollectFormView2
          getValue={(name) => values[name]}
          setValue={(name, value) => setValues({ ...values, [name]: value }, { name })}
          getErrors={(name) => errors?.[name] || {}}
          document={copyrightCollectDocument}
          karaokeOrder={karaokeOrder}
          onNext={() => {
            setPopup('RecipientNameLegalConfirm');
          }}
          onBack={() => {
            setView('collectionView1');
          }}
        />
      )}
      {view === 'collectionView3' && previewDocument !== null && (
        <CopyrightCollectFormView3
          getValue={(name) => values[name]}
          getItemCount={(collection) => karaokeAuthorValues.length}
          getItemValue={(collection, index, name) => karaokeAuthorValues[index][name]}
          document={previewDocument}
          karaokeOrder={karaokeOrder}
          onPreviewEmail={() => {
            setPopup('PreviewEmailPopup');
          }}
          onNext={() => {
            setAjax('executeCollect');
          }}
          onBack={() => {
            setView('collectionView2');
          }}
        />
      )}
      {view === 'independentView1' && (
        <IndependentCopyrightTransferFormView1
          document={independentCopyrightTransferDocument}
          getValue={(name) => values[name]}
          setValue={(name, value) => setValues({ ...values, [name]: value }, { name })}
          getErrors={(name) => errors?.[name] || {}}
          karaokeOrder={karaokeOrder}
          onNext={() => {
            setPopup('RecipientNameLegalConfirm');
          }}
          onBack={() => {
            setView('selectionView1');
          }}
        />
      )}
      {view === 'independentView2' && previewDocument !== null && (
        <IndependentCopyrightTransferFormView2
          document={previewDocument}
          getValue={(name) => values[name]}
          karaokeOrder={karaokeOrder}
          onNext={() => {
            setAjax('executeIndependent');
          }}
          onBack={() => {
            setView('independentView1');
          }}
        />
      )}
      {popup === 'confirm' && (
        <CopyrightAlreadyTransferredConfirmPopup
          onCancel={() => setPopup(null)}
          onConfirm={() => {
            setPopup(null);
            setView('independentView1');
          }}
        />
      )}
      {popup === 'PreviewEmailPopup' && previewEmailSubject !== null && previewEmailBody !== null && (
        <CopyrightCollectFormView4EmailPreviewPopup
          previewEmailSubject={previewEmailSubject}
          previewEmailBody={previewEmailBody}
          onClose={() => setPopup(null)}
        />
      )}
      {popup === 'RecipientNameLegalConfirm' && (
        <RecipientNameLegalConfirmPopup
          recipientName={karaokeOrder.applicant_name}
          onCancel={() => {
            setPopup(null);
          }}
          onConfirm={() => {
            setPopup(null);
            if (view === 'independentView1') {
              setAjax('validateIndependent');
            } else if (view === 'collectionView2') {
              setAjax('validateCollectStep2');
            }
          }}
        />
      )}
    </>
  );
}

